// eslint-disable-next-line @typescript-eslint/ban-ts-comment -- pi-lens/Deno resolver false positives on workspace package imports in this repo-native UI hook; app/toolchain resolution is correct
// @ts-nocheck
/**
 * useSDUIConfig — Client hook for fetching SDUI surface configs
 *
 * Calls the get-sdui-config Supabase Edge Function (fetch-all pattern)
 * and returns the surface config for a given key. Returns SDUI_DEFAULTS
 * synchronously on first render (no null states), then merges server
 * config on success.
 *
 * Deduplication: The edge function returns ALL surfaces in one response.
 * In-flight requests are keyed by userTier alone so multiple components
 * requesting different surfaces share a single HTTP call.
 *
 * Type safety: The surface key constrains the return type at compile time
 * via SDUISurfaceMap. `useSDUIConfig('blur-teaser')` returns SDUIBlurConfig.
 *
 * Returns: { config, isLoading, error }
 *   - config:    The surface config (NEVER null — defaults are always available)
 *   - isLoading: true while fetching
 *   - error:     Error object if the edge function call failed
 *
 * Task: S2 #1158
 * Agent: Diego <diego@liftping.net>
 */

import { useState, useEffect } from "react";
import {
	authenticatedFetch,
	getEdgeFunctionUrl,
} from "@dramadetector/api-client";
import {
	assertSupportedSduiSchemaVersion,
	isKnownSduiSurfaceKey,
} from "@dramadetector/shared";
import { sanitizeSduiSurfaceConfig } from "./schema";
import { SDUI_DEFAULTS } from "./sdui-defaults";
import type { SDUISurfaceMap, SDUISurfaceConfig } from "./sdui-defaults";
import type { UISpecTier } from "./use-ui-spec";

function reportSduiFallback(
	event: string,
	context: Record<string, unknown>,
): void {
	console.warn(`[useSDUIConfig] ${event}`, context);
}

const SDUI_FETCH_TIMEOUT_MS = 5000;

// ============================================================================
// Types
// ============================================================================

export interface UseSDUIConfigResult<T> {
	/** The surface config (NEVER null — defaults are always available) */
	config: T;
	/** True while the edge function call is in-flight */
	isLoading: boolean;
	/** Error object if the call failed */
	error: Error | null;
}

// ============================================================================
// Module-level cache
// ============================================================================

/** Cache TTL: 2 minutes — matches stated architecture for "instant-ish" propagation */
const CACHE_TTL_MS = 2 * 60 * 1000;

/**
 * All-surfaces cache keyed by userTier. Stores MERGED configs (defaults + server)
 * so cache-hit reads are consistent with first-mount reads. Expires after CACHE_TTL_MS.
 */
const allConfigsCache: Record<
	string,
	{ data: Record<string, SDUISurfaceConfig>; fetchedAt: number }
> = {};

/**
 * In-flight requests keyed by userTier alone — the edge function returns
 * ALL surfaces, so multiple hooks for different surfaces share one request.
 */
const inflightRequests: Record<
	string,
	Promise<Record<string, SDUISurfaceConfig>>
> = {};

export function __resetUseSduiConfigTestState() {
	for (const key of Object.keys(allConfigsCache)) {
		delete allConfigsCache[key];
	}
	for (const key of Object.keys(inflightRequests)) {
		delete inflightRequests[key];
	}
}

interface SduiConfigResponse {
	schemaVersion?: number | null;
	configs?: Record<string, unknown> | null;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function mergeKnownConfigsWithDefaults(
	configs: Record<string, unknown> | null | undefined,
	report?: (event: string, context: Record<string, unknown>) => void,
): Record<string, SDUISurfaceConfig> {
	const merged = Object.fromEntries(
		Object.entries(SDUI_DEFAULTS).map(([surfaceKey, defaults]) => [
			surfaceKey,
			{ ...defaults },
		]),
	) as Record<string, SDUISurfaceConfig>;

	if (!configs) {
		return merged;
	}

	for (const [surfaceKey, config] of Object.entries(configs)) {
		if (!isKnownSduiSurfaceKey(surfaceKey)) {
			report?.("ignored_unknown_surface", {
				surfaceKey,
			});
			continue;
		}

		if (!isPlainObject(config)) {
			report?.("ignored_non_object_surface_config", {
				surfaceKey,
				valueType: Array.isArray(config) ? "array" : typeof config,
			});
			continue;
		}

		const sanitizedConfig = sanitizeSduiSurfaceConfig(surfaceKey, config);
		if (!sanitizedConfig) {
			report?.("rejected_surface_config", {
				surfaceKey,
				reason: "no_valid_fields",
			});
			continue;
		}

		const droppedFieldCount =
			Object.keys(config).length - Object.keys(sanitizedConfig).length;
		if (droppedFieldCount > 0) {
			report?.("dropped_invalid_surface_fields", {
				surfaceKey,
				droppedFieldCount,
				acceptedFieldCount: Object.keys(sanitizedConfig).length,
			});
		}

		merged[surfaceKey] = {
			...SDUI_DEFAULTS[surfaceKey],
			...sanitizedConfig,
		} as SDUISurfaceConfig;
	}

	return merged;
}

// ============================================================================
// useSDUIConfig hook
// ============================================================================

/**
 * useSDUIConfig — Fetch an SDUI surface config from the get-sdui-config edge function
 *
 * @param surfaceKey  Typed SDUI surface key — constrains the return type
 * @param userTier    Server-verified user tier (from useTier())
 *
 * @returns { config, isLoading, error }
 */
export function useSDUIConfig<K extends keyof SDUISurfaceMap>(
	surfaceKey: K,
	userTier: UISpecTier,
): UseSDUIConfigResult<SDUISurfaceMap[K]> {
	type T = SDUISurfaceMap[K];

	// Defensive: treat undefined/null tier as 'free' to prevent spurious cache keys
	const safeTier: UISpecTier = userTier || "free";
	const defaults = SDUI_DEFAULTS[surfaceKey] as T;

	// Check if we already have fresh cached data for this tier
	const cacheEntry = allConfigsCache[safeTier];
	const isCacheFresh =
		cacheEntry && Date.now() - cacheEntry.fetchedAt < CACHE_TTL_MS;
	const cached = isCacheFresh
		? (cacheEntry.data[surfaceKey] as T | undefined)
		: undefined;

	const [config, setConfig] = useState<T>(() => cached ?? defaults);
	const [isLoading, setIsLoading] = useState(() => !cached);
	const [error, setError] = useState<Error | null>(null);

	// eslint-disable-next-line local-rules/no-race-conditions -- single fetch/caching effect with explicit mounted guard; shared per-tier inflight request is intentional
	useEffect(() => {
		// Already cached (merged) and fresh — no fetch needed
		const entry = allConfigsCache[safeTier];
		if (
			entry &&
			Date.now() - entry.fetchedAt < CACHE_TTL_MS &&
			entry.data[surfaceKey]
		) {
			setConfig(entry.data[surfaceKey] as T);
			setIsLoading(false);
			setError(null);
			return;
		}

		let isMounted = true;
		setIsLoading(true);

		async function fetchConfig() {
			try {
				const report = (event: string, context: Record<string, unknown>) => {
					reportSduiFallback(event, {
						surfaceKey,
						userTier: safeTier,
						...context,
					});
				};
				// Deduplicate: key by userTier alone (fetch-all returns all surfaces)
				if (!inflightRequests[safeTier]) {
					inflightRequests[safeTier] = (async () => {
						const url = getEdgeFunctionUrl("get-sdui-config");
						const controller = new AbortController();
						const timeoutId = setTimeout(() => {
							controller.abort();
						}, SDUI_FETCH_TIMEOUT_MS);

						try {
							// Public endpoint (per workers/src/index.ts allowlist) — pass
							// skipAuth so we don't fire a Bearer-tagged request during the
							// pre-JWT window, which the auth middleware rejects with 401
							// before the handler ever runs. Eliminates one of the bootstrap
							// 401s observed on staging.
							const response = await authenticatedFetch(url, {
								method: "POST",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify({ userTier: safeTier }),
								skipAuth: true,
								signal: controller.signal,
							});

							if (!response.ok) {
								const errBody = await response.json().catch(() => ({}));
								throw new Error(
									`get-sdui-config error: ${(errBody as Record<string, string>)?.error || response.statusText}`,
								);
							}

							const data = (await response.json()) as SduiConfigResponse;
							assertSupportedSduiSchemaVersion(data?.schemaVersion);
							return mergeKnownConfigsWithDefaults(data?.configs, report);
						} catch (error) {
							const isAbortError =
								((typeof DOMException !== "undefined" &&
									error instanceof DOMException &&
									error.name === "AbortError") ||
									(typeof error === "object" &&
										error !== null &&
										"name" in error &&
										(error as { name?: unknown }).name === "AbortError"));
							if (isAbortError) {
								throw new Error(
									`get-sdui-config timed out after ${SDUI_FETCH_TIMEOUT_MS}ms`,
								);
							}
							throw error;
						} finally {
							clearTimeout(timeoutId);
						}
					})();
					// Clean up cached promise on rejection so subsequent mounts don't await a rejected promise.
					// Separate line (not chained on assignment) to avoid swallowing the error for the primary awaiter.
					inflightRequests[safeTier]!.catch(() => {
						delete inflightRequests[safeTier];
					});
				}

				const allConfigs = await inflightRequests[safeTier]!;

				if (!isMounted) return;

				allConfigsCache[safeTier] = {
					data: allConfigs,
					fetchedAt: Date.now(),
				};
				// Delete inflight AFTER cache is populated to prevent race where
				// a concurrent mount sees no inflight AND no cache entry.
				delete inflightRequests[safeTier];

				// Update this hook's state with the merged config
				setConfig((allConfigs[surfaceKey] as T | undefined) ?? defaults);
				setError(null);
				setIsLoading(false);
			} catch (err) {
				// Clean up inflight on error too
				delete inflightRequests[safeTier];

				if (!isMounted) return;

				// On error, keep using SDUI_DEFAULTS (zero regression)
				const e = err instanceof Error ? err : new Error(String(err));
				reportSduiFallback("fetch_failed_using_defaults", {
					surfaceKey,
					userTier: safeTier,
					error: e.message,
				});
				setError(e);
				setIsLoading(false);
			}
		}

		fetchConfig();

		return () => {
			isMounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [surfaceKey, safeTier]);

	return { config, isLoading, error };
}
